// Slack-style conversation sidebar — the primary nav (Matt: "just like Slack").
// One deliberate divergence for Helm's agent-first positioning: YOUR AGENTS sit
// at the top, above channels and people DMs. Persistent beside the conversation.
import { useState } from 'react';
import { Agent, Channel, Dm } from './api';
import { AgentFace } from './AgentAvatar';
import { avatarStyle } from './AccountMenu';
import { Icon } from './icons';

function Section({ title, onAdd, children }: { title: string; onAdd?: () => void; children: React.ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="fx-sb-section">
      <div className="fx-sb-head">
        <button type="button" className="fx-sb-head-btn" onClick={() => setOpen((v) => !v)}>
          <span className={`fx-sb-caret${open ? ' is-open' : ''}`}>▸</span> {title}
        </button>
        {onAdd && <button type="button" className="fx-sb-add" title={`Add ${title.toLowerCase()}`} onClick={onAdd}>＋</button>}
      </div>
      {open && <div className="fx-sb-list">{children}</div>}
    </div>
  );
}

export function ConversationSidebar({
  agents, channels, dms, activeId, meId,
  onOpenChannel, onOpenAgent, onOpenDm, onNewChannel, onNewDm,
}: {
  agents: Agent[];
  channels: Channel[];
  dms: Dm[];
  activeId: string | null;
  meId?: string;
  onOpenChannel: (id: string) => void;
  onOpenAgent: (agentId: string) => void;
  onOpenDm: (id: string) => void;
  onNewChannel?: () => void;
  onNewDm?: () => void;
}) {
  // Agent DMs are keyed by the agent they're with, so an agent row can carry the
  // unread/active state of its existing DM conversation.
  const dmByAgent = new Map<string, Dm>();
  const peopleDms: Dm[] = [];
  for (const d of dms) {
    if (d.dm_with?.type === 'agent' && d.dm_with.id) dmByAgent.set(d.dm_with.id, d);
    else peopleDms.push(d);
  }

  const Row = ({ active, unread, count, glyph, glyphStyle, label, onClick }: {
    active: boolean; unread?: boolean; count?: number;
    glyph: React.ReactNode; glyphStyle?: React.CSSProperties; label: string; onClick: () => void;
  }) => (
    <button type="button" className={`fx-sb-row${active ? ' is-active' : ''}${unread ? ' is-unread' : ''}`} onClick={onClick}>
      <span className="fx-sb-glyph" style={glyphStyle}>{glyph}</span>
      <span className="fx-sb-name">{label}</span>
      {!!count && count > 0 && <span className="fx-sb-badge">{count > 9 ? '9+' : count}</span>}
    </button>
  );

  return (
    <nav className="fx-sidebar" aria-label="Conversations">
      {agents.length > 0 && (
        <Section title="Your agents">
          {agents.map((a) => {
            const dm = dmByAgent.get(a.id);
            return (
              <Row key={a.id}
                active={!!dm && dm.id === activeId}
                unread={!!dm?.unread}
                count={dm?.mentions || 0}
                glyph={<AgentFace name={a.name} avatarUrl={a.avatar_url} size={20} />}
                glyphStyle={{ background: 'transparent' }}
                label={a.name}
                onClick={() => (dm ? onOpenDm(dm.id) : onOpenAgent(a.id))}
              />
            );
          })}
        </Section>
      )}

      <Section title="Channels" onAdd={onNewChannel}>
        {channels.length === 0 && <div className="fx-sb-empty">No channels yet</div>}
        {channels.map((c) => (
          <Row key={c.id}
            active={c.id === activeId}
            unread={!!c.unread}
            count={c.mentions || 0}
            glyph={<span className="fx-sb-hash">#</span>}
            label={c.name || 'channel'}
            onClick={() => onOpenChannel(c.id)}
          />
        ))}
      </Section>

      <Section title="Direct messages" onAdd={onNewDm}>
        {peopleDms.length === 0 && <div className="fx-sb-empty">No direct messages</div>}
        {peopleDms.map((d) => {
          const u = d.dm_with;
          const hasAvatar = !!u?.avatar_url;
          return (
            <Row key={d.id}
              active={d.id === activeId}
              unread={!!d.unread}
              count={d.mentions || 0}
              // Consistent circular avatar: photo if present, else a colored initial.
              glyph={hasAvatar ? '' : ((d.name || '?').trim()[0] || '?').toUpperCase()}
              glyphStyle={{
                ...(u ? avatarStyle(u) : {}),
                borderRadius: '50%',
                backgroundColor: 'hsl(var(--h, 210) 48% 46%)',
                color: hasAvatar ? 'transparent' : '#fff',
                fontSize: '10px',
              }}
              label={(u?.type === 'user' && u.id === meId) ? `${d.name} (you)` : (d.name || 'Direct message')}
              onClick={() => onOpenDm(d.id)}
            />
          );
        })}
      </Section>
    </nav>
  );
}
